import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ManageSearch as ManageSearchIcon } from '@frontierag/frontier-ui-library/icons';
import {
  GROUPED_PRODUCTS_TYPES,
  LOAD_VIRTUAL_PACKS_LIMIT,
  VIRTUAL_PACKS_LIMIT_RESULT,
} from 'appConstants';
import DynamicAutocomplete from 'components/DynamicAutocomplete';
import { useProducts } from 'contexts/Products';

import { CropSelectOption, GroupedProductSelect } from 'components';
import type { IVirtualPackSelect } from './types';

export default function VirtualPackSelect({
  disabled,
  selectedProductReference,
  textFieldProps,
  limitResult = VIRTUAL_PACKS_LIMIT_RESULT,
  onChange = () => {},
}: IVirtualPackSelect) {
  const { t } = useTranslation('VirtualPackSelect');

  const {
    state,
    selectors: { selectVirtualPack },
    actions: { loadVirtualPacks },
  } = useProducts();

  const { error, loaded, data: virtualPacks, loading } = state.virtualPacks;
  const selectedVirtualPack =
    selectVirtualPack(state.virtualPacks, selectedProductReference) || null;

  useEffect(() => {
    if (!loaded && !loading && !error) {
      loadVirtualPacks({ limit: LOAD_VIRTUAL_PACKS_LIMIT });
    }
  }, [error, loading, loadVirtualPacks, loaded]);

  const handleOnChange = useCallback(
    (event, value) => {
      onChange(value);
    },
    [onChange],
  );

  return (
    <DynamicAutocomplete
      disabled={disabled}
      textFieldProps={{
        label: t('labels.product'),
        placeholder: t('placeholders.searchForVirtualPack'),
        ...textFieldProps,
      }}
      startAdornmentIcon={<ManageSearchIcon />}
      noOptionsText={t('labels.noOptions')}
      value={selectedVirtualPack}
      options={virtualPacks}
      getOptionLabel={({ packagedItemDescription }) => packagedItemDescription}
      onChange={handleOnChange}
      loading={loading}
      groupBy={() => ''}
      displayPopupIcon
      filterOptionsProps={{
        limit: limitResult,
        stringify: ({ packagedItemReference, packagedItemDescription }) =>
          `${packagedItemDescription} ${packagedItemReference}`,
      }}
      renderOption={(props, { packagedItemDescription, packagedItemReference }) => (
        <CropSelectOption
          key={props.id}
          props={props}
          itemDescription={packagedItemDescription}
          itemReference={packagedItemReference}
        />
      )}
      renderGroup={(params) => (
        <GroupedProductSelect
          key={params.key}
          group={GROUPED_PRODUCTS_TYPES.VIRTUAL_PACKS}
          transNamespace="VirtualPackSelect"
        >
          {params.children}
        </GroupedProductSelect>
      )}
      isOptionEqualToValue={(option, value) => {
        return option.packagedItemReference === value.packagedItemReference;
      }}
    />
  );
}
